import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { precisionOf, starsOf, useCountdown, TimerBar, Hud, Flash, PRACTICE_SECONDS, FLASH_MS } from "./nqShared";

const CFG = {
  1: { seconds: 45, coinEvery: 1600, coinShow: 1300, target: "🪙", others: [], gaugeStep: 6, switchAt: 0 },
  2: { seconds: 60, coinEvery: 1300, coinShow: 1100, target: "🪙", others: ["💎"], gaugeStep: 8, switchAt: 0 },
  3: { seconds: 70, coinEvery: 1100, coinShow: 950, target: "🪙", others: ["💎", "🔔"], gaugeStep: 10, switchAt: 35 },
};

export const brief = (level) => {
  const c = CFG[level.n];
  return {
    title: "Deux missions en même temps",
    goal: `attraper les ${c.target} ET garder la jauge hors du rouge.`,
    regarde: "Des objets apparaissent dans la grille. À côté, une jauge monte petit à petit.",
    fais: `Touche les ${c.target} dès qu'ils apparaissent. Quand la jauge devient rouge, appuie sur ⚙️ Stabiliser.`,
    attention: `N'appuie sur Stabiliser que quand la jauge est rouge.${c.others.length ? ` Les ${c.others.join(" ")} sont des pièges : on ne les touche pas.` : ""}${c.switchAt ? " Au niveau Maître, l'objet à attraper change en cours de mission !" : ""}`,
    preview: { ok: [c.target, "⚙️ si rouge"], ko: c.others },
  };
};

export default function GameMultitasker({ power, level, onFinish, practice, onPracticeEnd }) {
  const cfg = useMemo(() => (practice ? { ...CFG[level.n], seconds: PRACTICE_SECONDS, switchAt: 0 } : CFG[level.n]), [level, practice]);
  const [phase, setPhase] = useState("play");
  const [coin, setCoin] = useState(null);
  const [gauge, setGauge] = useState(40);
  const [target, setTarget] = useState(cfg.target);
  const [stats, setStats] = useState({ coins: 0, got: 0, wrong: 0, alerts: 0, fixed: 0 });
  const [flash, setFlash] = useState(null);
  const targetRef = useRef(cfg.target);
  const inRed = gauge >= 80;
  const running = phase === "play";

  const finish = useCallback(() => {
    if (practice) return onPracticeEnd();
    const total = stats.coins + stats.alerts;
    const hits = stats.got + stats.fixed;
    const precision = precisionOf(hits, total, stats.wrong);
    return onFinish({ hits, total, errors: stats.wrong, precision, stars: starsOf(precision), detail: `${stats.got}/${stats.coins} ${target} attrapés · ${stats.fixed}/${stats.alerts} alertes stabilisées · ${stats.wrong} mauvais objet(s)` });
  }, [stats, target, onFinish, practice, onPracticeEnd]);
  const left = useCountdown(cfg.seconds, running, finish);

  useEffect(() => {
    if (!running) return undefined;
    const tick = () => {
      const isTarget = cfg.others.length === 0 || Math.random() < 0.65;
      const emoji = isTarget ? targetRef.current : cfg.others[Math.floor(Math.random() * cfg.others.length)];
      setCoin({ emoji, isTarget, cell: Math.floor(Math.random() * 9), key: Date.now() });
      if (isTarget) setStats((s) => ({ ...s, coins: s.coins + 1 }));
      setTimeout(() => setCoin(null), cfg.coinShow);
    };
    tick();
    const id = setInterval(tick, cfg.coinEvery);
    return () => clearInterval(id);
  }, [running, cfg]);

  useEffect(() => {
    if (!running) return undefined;
    const id = setInterval(() => setGauge((g) => {
      const n = Math.min(100, g + cfg.gaugeStep * (0.6 + Math.random()));
      if (g < 80 && n >= 80) setStats((s) => ({ ...s, alerts: s.alerts + 1 }));
      return n;
    }), 1000);
    return () => clearInterval(id);
  }, [running, cfg]);

  useEffect(() => {
    if (!running || !cfg.switchAt) return undefined;
    const id = setTimeout(() => { targetRef.current = "💎"; setTarget("💎"); setPhase("switch"); setTimeout(() => setPhase("play"), 2000); }, cfg.switchAt * 1000);
    return () => clearTimeout(id);
  }, [running, cfg]);

  const tapCell = (i) => {
    if (!running || !coin || coin.cell !== i) return;
    if (coin.isTarget) { setStats((s) => ({ ...s, got: s.got + 1 })); setFlash("ok"); } else { setStats((s) => ({ ...s, wrong: s.wrong + 1 })); setFlash("ko"); }
    setCoin(null); setTimeout(() => setFlash(null), FLASH_MS);
  };
  const stabilise = () => { if (!running) return; if (inRed) { setStats((s) => ({ ...s, fixed: s.fixed + 1 })); setGauge(30); setFlash("ok"); } else setFlash("ko"); setTimeout(() => setFlash(null), FLASH_MS); };

  return (
    <div className="space-y-4" data-testid="nq-game-multitasker">
      <TimerBar left={left} total={cfg.seconds} color={power.color} />
      <Hud items={[["Objet :", target, power.color], ["Attrapés", `${stats.got}/${stats.coins}`, "#7CC6A6"], ["Alertes stabilisées", `${stats.fixed}/${stats.alerts}`, "#5E9BD6"], ["Erreurs", stats.wrong, "#E0705C"]]} />
      <div className="relative grid sm:grid-cols-[1fr_auto] gap-4">
        {phase === "switch" && <div className="absolute inset-0 z-10 rounded-3xl bg-[#ECE6F7]/95 flex flex-col items-center justify-center text-center p-6 animate-fade-up" data-testid="nq-mt-switch"><div className="text-5xl mb-2">🔄</div><div className="font-serif-title text-2xl text-[#5E4A9C]">Changement de règle !</div><p>Maintenant, attrape les <strong>💎</strong> et ignore les 🪙.</p></div>}
        <Flash kind={flash} />
        <div className="grid grid-cols-3 gap-2 rounded-3xl bg-[#FDE4DF] p-3">
          {Array.from({ length: 9 }, (_, i) => (
            <button key={i} onClick={() => tapCell(i)} data-testid={`nq-mt-cell-${i}`} className="aspect-square rounded-2xl bg-white flex items-center justify-center text-4xl shadow-sm active:scale-95 transition-transform">
              {coin && coin.cell === i ? <span key={coin.key} className="animate-fade-up" data-testid={coin.isTarget ? "nq-mt-target" : "nq-mt-other"}>{coin.emoji}</span> : ""}
            </button>
          ))}
        </div>
        <div className="rounded-3xl bg-[#1F2A44] p-4 flex flex-col items-center gap-3 sm:w-40">
          <div className="text-white text-xs font-bold uppercase tracking-wider">{inRed ? "🚨 Rouge !" : "Jauge"}</div>
          <div className="w-10 h-40 rounded-full bg-white/20 overflow-hidden flex flex-col justify-end" data-testid="nq-mt-gauge">
            <div className="w-full transition-all duration-700" style={{ height: `${gauge}%`, background: inRed ? "#E0705C" : gauge > 60 ? "#F2B84B" : "#7CC6A6" }} />
          </div>
          <button onClick={stabilise} data-testid="nq-mt-stabilise" className={`w-full py-3 rounded-2xl font-black text-sm text-white transition-all ${inRed ? "bg-[#E0705C] animate-pulse scale-105" : "bg-white/20"}`}>⚙️ Stabiliser</button>
        </div>
      </div>
    </div>
  );
}
